import { useMutation, UseMutationOptions } from '@tanstack/react-query'
import axios, { AxiosResponse } from 'axios'
import { z } from 'zod'
import { DecisionSchema } from './DecisionSchema'
import { REASONING_VALUES } from './REASONING_VALUES'

type ReasoningString = keyof typeof REASONING_VALUES

export const ReasoningInputSchema = z.enum(
  ['No selection', 'Based on evidence', 'Based on program rules', 'Other'],
  {
    errorMap: () => ({ message: 'Please select a valid reasoning.' }),
  }
)

export type ReasoningInput = z.infer<typeof ReasoningInputSchema>

export const ReasoningSchema = ReasoningInputSchema.transform((val) => {
  return REASONING_VALUES[val]
})

type Decision = z.infer<typeof DecisionSchema>

const decisionToApiValue = {
  accepted: 1,
  rejected: 2,
} as const

const PayloadSchema = z.object({
  decision: DecisionSchema,
  program_id: z.number(),
  paymentId: z.number(),
  justification: z.string(),
  reasoning: ReasoningInputSchema,
  public_reply: z.string().nullable(),
})

const APIPayloadSchema = z.object({
  paymentId: z.number(),
  program_id: z.number(),
  status: z.literal(1).or(z.literal(2)),
  justification: z.string(),
  reasoning: z.number(),
  public_reply: z.string().nullable(),
})

type Payload = z.infer<typeof PayloadSchema>
type APIPayload = z.infer<typeof APIPayloadSchema>

function transformPayload(payload: Payload): APIPayload {
  return {
    program_id: payload.program_id,
    paymentId: payload.paymentId,
    status: decisionToApiValue[payload.decision],
    justification: payload.justification,
    reasoning: REASONING_VALUES[payload.reasoning],
    public_reply: payload.public_reply,
  }
}

function updateDecision(payload: APIPayload) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return axios.put<any, AxiosResponse<{ success: boolean; message: string }>>(
    `/payment_accept_reject/${payload.paymentId}`,
    payload
  )
}

type SubmitPaymentDecisionOptions = Omit<
  UseMutationOptions<
    AxiosResponse<{ success: boolean; message: string }>,
    Error,
    Payload,
    unknown
  >,
  'mutationFn'
>

function useSubmitPaymentDecision(options?: SubmitPaymentDecisionOptions) {
  return useMutation({
    mutationFn: async (payload: Payload) => {
      const validatedPayload = PayloadSchema.parse(payload)
      const apiPayload = transformPayload(validatedPayload)
      return updateDecision(apiPayload)
    },
    ...options,
  })
}

// eslint-disable-next-line
export { useSubmitPaymentDecision }
export type { Decision, Payload, ReasoningString }
